import { useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  getRedirectResult,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { captureError } from '../services/sentry';

const googleProvider = new GoogleAuthProvider();

export default function useAuth() {
  const [user, setUser] = useState(null);
  const [linkedProfile, setLinkedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Look up the Firestore users doc linked to a Firebase Auth UID
  const fetchLinkedProfile = useCallback(async (authUid) => {
    try {
      const q = query(collection(db, 'users'), where('authUid', '==', authUid));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        setLinkedProfile({ id: docSnap.id, ...docSnap.data() });
        return { id: docSnap.id, ...docSnap.data() };
      }
      setLinkedProfile(null);
      return null;
    } catch (e) {
      console.error('Error fetching linked profile:', e);
      setLinkedProfile(null);
      return null;
    }
  }, []);

  // Find an existing users doc by email and link it to the auth UID
  const linkExistingProfile = useCallback(async (email, authUid) => {
    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const existingDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'users', existingDoc.id), { authUid });
        const profile = { id: existingDoc.id, ...existingDoc.data(), authUid };
        setLinkedProfile(profile);
        return profile;
      }
      return null;
    } catch (e) {
      console.error('Error linking existing profile:', e);
      return null;
    }
  }, []);

  // Process any pending redirect result from signInWithRedirect.
  // On mobile, Google sign-in goes through a full-page redirect; without this
  // explicit call, the credential returned in the URL never gets exchanged
  // and onAuthStateChanged stays on the old (anonymous) user.
  useEffect(() => {
    getRedirectResult(auth).catch((e) => {
      if (e?.code !== 'auth/no-redirect-result') {
        console.error('Redirect-result processing failed:', e);
      }
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // No session yet — sign in anonymously so every client has a uid.
        // Firestore/Storage rules require auth; this keeps the guest flow working.
        try {
          await signInAnonymously(auth);
        } catch (e) {
          console.error('Anonymous sign-in failed:', e);
          captureError(e, { where: 'signInAnonymously' });
          setLoading(false);
        }
        return; // onAuthStateChanged will fire again with the anon user
      }
      setUser(firebaseUser);

      if (firebaseUser.isAnonymous) {
        setLinkedProfile(null);
        setLoading(false);
        return;
      }

      // Real user (email/password or Google) — make sure they have a Firestore
      // profile linked to their authUid. Order:
      //   1. existing profile already linked by authUid — done.
      //   2. existing profile with matching email but no authUid — link it.
      //   3. OAuth user with a displayName — auto-create a profile from the
      //      provider data. (Email/password sign-up doesn't hit this branch
      //      because displayName is null; signUp() creates the profile with
      //      the user-typed name explicitly.)
      let profile = await fetchLinkedProfile(firebaseUser.uid);
      if (!profile && firebaseUser.email) {
        profile = await linkExistingProfile(firebaseUser.email, firebaseUser.uid);
      }
      if (!profile && firebaseUser.displayName) {
        try {
          const newProfileData = {
            name: firebaseUser.displayName,
            email: firebaseUser.email || '',
            birthday: '',
            authUid: firebaseUser.uid,
            createdAt: new Date(),
          };
          const docRef = await addDoc(collection(db, 'users'), newProfileData);
          setLinkedProfile({ id: docRef.id, ...newProfileData });
        } catch (e) {
          console.error('Could not auto-create profile from OAuth user:', e);
        }
      }

      setLoading(false);
    });
    return unsubscribe;
  }, [fetchLinkedProfile, linkExistingProfile]);

  const signUp = useCallback(async (email, password, name, birthday, createUserFn) => {
    setError(null);
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const authUid = credential.user.uid;

      // Check if a guest profile already exists with this email
      const existing = await linkExistingProfile(email, authUid);
      if (!existing && createUserFn) {
        // Create a new Firestore users doc linked to auth
        const newProfile = await createUserFn(name, email, birthday, authUid);
        setLinkedProfile(newProfile);
      }
      return credential.user;
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, [linkExistingProfile]);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      await fetchLinkedProfile(credential.user.uid);
      return credential.user;
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, [fetchLinkedProfile]);

  // Trigger only — profile linking and creation happen in the onAuthStateChanged
  // effect. We use popup on every platform: iOS Safari's ITP blocks cookies
  // between the Firebase auth handler domain and the app domain, breaking the
  // redirect flow. Popup uses postMessage between windows so it sidesteps the
  // cross-origin cookie issue. Accepts but ignores a createUserFn arg for
  // backwards compatibility with existing callers.
  const signInWithGoogle = useCallback(async (_createUserFn) => { // eslint-disable-line no-unused-vars
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
      setLinkedProfile(null);
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  const resetPassword = useCallback(async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  return {
    user,
    linkedProfile,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
  };
}
